import * as THREE from 'three/webgpu';
import {normalizeArcAngles} from '../../tool/normalizeArcAngles';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';

/**
 * 解析三元组坐标。
 *
 * @param value 候选值。
 * @returns 合法时返回三元组；否则 null。
 */
function parseVector3Tuple(value: unknown): readonly [number, number, number] | null {
    if (!Array.isArray(value) || value.length < 3) {
        return null;
    }

    const [x, y, z] = value;

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        return null;
    }

    return [Number(x), Number(y), Number(z)];
}

/**
 * 按槽口参数记录采样中心线点列（直槽口两端点；圆弧槽口沿中心弧采样）。
 *
 * 设计说明：
 * **全仓唯一一份**中心线采样实现——绘制预览、正式辅助线、尺寸驱动写回后的刷新、
 * Undo/Redo 恢复后的刷新都消费本函数。此前采样只活在绘制处理器里，
 * 尺寸驱动改完几何没人重采样，屏幕上就残留旧弧度的中心线（用户可见的"错乱线条"）。
 *
 * @param geometryData 槽口参数记录（世界坐标元组 + 弧参数）。
 * @param fallbackMode 记录缺失 mode 时的回退模式（预览态使用）。
 * @returns 世界坐标点列；数据不足以定义中心线时返回 null。
 */
export function buildSlotCenterlinePoints(
    geometryData: Record<string, unknown>,
    fallbackMode?: string,
): THREE.Vector3[] | null {
    const startTuple = parseVector3Tuple(geometryData.centerlineStart);
    const endTuple = parseVector3Tuple(geometryData.centerlineEnd);

    if (!startTuple || !endTuple) {
        return null;
    }

    const start = new THREE.Vector3(...startTuple);
    const end = new THREE.Vector3(...endTuple);
    const mode = String(geometryData.mode ?? fallbackMode ?? 'sketch-slot');

    if (mode !== 'sketch-slot-3-point-arc' && mode !== 'sketch-slot-center-arc') {
        return [start, end];
    }

    const centerTuple = parseVector3Tuple(geometryData.center);
    const xAxisTuple = parseVector3Tuple(geometryData.xAxis);
    const yAxisTuple = parseVector3Tuple(geometryData.yAxis);
    const radius = Number(geometryData.radius);
    const startAngle = Number(geometryData.startAngle);
    const endAngle = Number(geometryData.endAngle);
    const clockwise = Boolean(geometryData.clockwise);

    if (
        !centerTuple
        || !xAxisTuple
        || !yAxisTuple
        || !Number.isFinite(radius)
        || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.slotMinimumRadius
        || !Number.isFinite(startAngle)
        || !Number.isFinite(endAngle)
    ) {
        return [start, end];
    }

    const center = new THREE.Vector3(...centerTuple);
    const xAxis = new THREE.Vector3(...xAxisTuple).normalize();
    const yAxis = new THREE.Vector3(...yAxisTuple).normalize();
    const normalized = normalizeArcAngles(startAngle, endAngle, clockwise);
    const steps = Math.max(20, Math.ceil((normalized.angleRange / (Math.PI * 2)) * 72));
    const points: THREE.Vector3[] = [];

    for (let i = 0; i <= steps; i += 1) {
        const t = i / steps;
        const angle = clockwise
            ? normalized.start - normalized.angleRange * t
            : normalized.start + normalized.angleRange * t;
        const direction = xAxis.clone()
            .multiplyScalar(Math.cos(angle))
            .addScaledVector(yAxis, Math.sin(angle));

        points.push(center.clone().addScaledVector(direction, radius));
    }

    return points.length >= 2 ? points : [start, end];
}
